/**
 * Dev-only live-chat hours override client (dev settings drawer).
 *
 * Drives the backend's test override (TRAC-790, TRAC-1030): GET/POST
 * /agent/dev/live-chat-hours. The override lives in each REGION's Redis, so a flip
 * must reach both regions or the Live Chat button depends on which one Global
 * Accelerator happens to route to. Never runs in a prod build, and the backend
 * 404s in prod as well. On a deployed env the backend wants an Admin/QA caller
 * (TRACI-521), so every call carries the chat's token when there is one.
 *
 * TRACI-555: the switch is per browser. Every call names this browser's scope id
 * (made on first use, kept in localStorage), so a flip changes only the chat that sends
 * that id as X-Hours-Scope (hoursScopeHeaders, merged by getRequestHeaders), never
 * another tester's. The site's chat on stage and the ODE holds a shopper token, which
 * the backend refuses; there the QA extension, when installed, makes the same call from
 * its service worker with the tester's own QA token (extension devtools.js). The token
 * never enters this page.
 *
 * TRACI-598: the extension is no longer needed. The drawer first calls
 * /agent/dev/live-chat-hours/scoped, which takes the chat's own token (a shopper's
 * included) for this one browser, with the scope in the X-Hours-Scope header only. A
 * backend from before that route (404) gets the old ?scope= call; a refusal (401/403)
 * still falls back to the extension relay.
 */
import { getRegionApiUrl } from '../config/backend';
import { isProdBuild } from './handoffProviderOverride';

export type HoursOverride = 'real' | 'open' | 'closed';

export interface RegionHours {
  base: string;
  override: HoursOverride | null;
  expiresInSeconds: number | null;
  /** What the whole environment is forced to (someone ran dev.sh or curl), if anything. */
  shared?: HoursOverride | null;
  error?: string;
}

export const HOURS_CHANGED_EVENT = 'traci:live-chat-hours-changed';

export type GetToken = () => Promise<string | undefined>;

const PATH = '/agent/dev/live-chat-hours';
const SCOPED_PATH = '/agent/dev/live-chat-hours/scoped';

function isOverride(v: unknown): v is HoursOverride {
  return v === 'real' || v === 'open' || v === 'closed';
}

/** Both region-pinned hosts (one host locally, where there is no region split). */
export function hoursBases(): string[] {
  const bases = [getRegionApiUrl('us-east-1'), getRegionApiUrl('us-west-2')];
  return Array.from(new Set(bases));
}

function errorFor(status: number): string {
  if (status === 404) return 'not available';
  if (status === 401 || status === 403) return 'sign in as a tester to change this';
  if (status === 429) return 'too many changes; try again in a few minutes';
  return `HTTP ${status}`;
}

export const HOURS_SCOPE_STORAGE_KEY = 'traci_dev_hours_scope';
export const HOURS_SCOPE_HEADER = 'X-Hours-Scope';
const SCOPE_RE = /^[A-Za-z0-9_-]{16,64}$/;

// The id for this page when storage is blocked (a third-party iframe in some browsers):
// without it every call would make a new id, and a flip would set one id and read another.
let memoryScope: string | null = null;
// TRACI-598: storage refused us, so the override lasts only as long as this page.
let storageBlocked = false;

function storedScope(): string | null {
  try {
    const s = localStorage.getItem(HOURS_SCOPE_STORAGE_KEY);
    if (s && SCOPE_RE.test(s)) return s;
  } catch { storageBlocked = true; /* fall back to this page's copy */ }
  return memoryScope;
}

/** True once this browser refused to store the scope id (Safari and some third-party
 * iframes): the drawer says the setting lasts until the page reloads. */
export function hoursScopeStorageBlocked(): boolean {
  return storageBlocked;
}

/** This browser's scope id, made (128 random bits) and kept the first time it is needed. */
export function hoursScope(): string {
  const have = storedScope();
  if (have) return have;
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const made = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  memoryScope = made;
  try { localStorage.setItem(HOURS_SCOPE_STORAGE_KEY, made); } catch { storageBlocked = true; /* this page's copy stands */ }
  return made;
}

/** Test seam: forget this page's copy. */
export function _resetHoursScopeForTests(): void {
  memoryScope = null;
  storageBlocked = false;
  relayBases.clear();
  legacyBases.clear();
}

/** The header for the chat's own requests; empty in a prod build or before any flip here. */
export function hoursScopeHeaders(): Record<string, string> {
  if (isProdBuild()) return {};
  const scope = storedScope();
  return scope ? { [HOURS_SCOPE_HEADER]: scope } : {};
}

// The QA extension marks a TRACI frame it can relay for (tools/ai-agent-preview/devtools.js).
const EXT_REQ = 'TRACI_DEVTOOL_REQ';
const EXT_RES = 'TRACI_DEVTOOL_RES';
const EXT_TIMEOUT_MS = 15000;
// Hosts that refused this chat's own token: ask the extension straight away next time.
const relayBases = new Set<string>();
// Hosts without the TRACI-598 scoped route (it answered 404): use the old route there.
const legacyBases = new Set<string>();

function extensionPresent(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.traciQaExt === '1';
}

type Reply = { status: number; data: unknown };

function viaExtension(base: string, scope: string, state?: HoursOverride): Promise<Reply | null> {
  return new Promise((resolve) => {
    const id = `h${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const done = (r: Reply | null) => {
      window.removeEventListener('message', onMsg);
      clearTimeout(timer);
      resolve(r);
    };
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (e.source !== window || !d || d.type !== EXT_RES || d.id !== id) return;
      done(typeof d.status === 'number' ? { status: d.status, data: d.data } : null);
    };
    const timer = setTimeout(() => done(null), EXT_TIMEOUT_MS);
    window.addEventListener('message', onMsg);
    window.postMessage({ type: EXT_REQ, id, base, scope, ...(state ? { state } : {}) }, window.location.origin);
  });
}

async function direct(base: string, scope: string, state?: HoursOverride, getToken?: GetToken): Promise<Reply> {
  const token = getToken ? await getToken() : undefined;
  if (!legacyBases.has(base)) {
    const res = await fetch(`${base}${SCOPED_PATH}${state ? `?${new URLSearchParams({ state })}` : ''}`, {
      method: state ? 'POST' : 'GET',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), [HOURS_SCOPE_HEADER]: scope },
    });
    if (res.status !== 404) return { status: res.status, data: res.ok ? await res.json() : null };
    legacyBases.add(base);
  }
  const q = new URLSearchParams({ scope, ...(state ? { state } : {}) });
  const res = await fetch(`${base}${PATH}?${q}`, {
    method: state ? 'POST' : 'GET',
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });
  return { status: res.status, data: res.ok ? await res.json() : null };
}

async function callRegion(base: string, state?: HoursOverride, getToken?: GetToken): Promise<RegionHours> {
  try {
    const scope = hoursScope();
    const ext = extensionPresent();
    let r: Reply | null = ext && relayBases.has(base) ? await viaExtension(base, scope, state) : null;
    if (!r) r = await direct(base, scope, state, getToken);
    // A shopper-token chat (the site's own, on stage or the ODE): ask the tester's extension.
    if ((r.status === 401 || r.status === 403) && ext && !relayBases.has(base)) {
      const relayed = await viaExtension(base, scope, state);
      if (relayed) {
        relayBases.add(base);
        r = relayed;
      }
    }
    if (r.status < 200 || r.status >= 300) {
      return { base, override: null, expiresInSeconds: null, error: errorFor(r.status) };
    }
    const d = r.data as { scope?: unknown; override?: unknown; expires_in_seconds?: unknown; shared_override?: unknown } | null;
    // A backend from before TRACI-555 ignores `scope` and would have flipped the whole
    // environment; the drawer must not show that as this browser's own setting.
    if (d?.scope !== scope) {
      return { base, override: null, expiresInSeconds: null, error: 'backend does not support per-browser hours yet' };
    }
    return {
      base,
      override: isOverride(d?.override) ? d.override : null,
      expiresInSeconds: typeof d?.expires_in_seconds === 'number' ? d.expires_in_seconds : null,
      shared: isOverride(d?.shared_override) ? d.shared_override : null,
    };
  } catch (e) {
    return { base, override: null, expiresInSeconds: null, error: e instanceof Error ? e.message : 'request failed' };
  }
}

/** Read every region. Empty in a prod build, without touching the network. */
export async function readHours(getToken?: GetToken): Promise<RegionHours[]> {
  if (isProdBuild()) return [];
  return Promise.all(hoursBases().map((b) => callRegion(b, undefined, getToken)));
}

/** Set every region. Empty in a prod build, without touching the network. */
export async function setHours(state: HoursOverride, getToken?: GetToken): Promise<RegionHours[]> {
  if (isProdBuild()) return [];
  const out = await Promise.all(hoursBases().map((b) => callRegion(b, state, getToken)));
  window.dispatchEvent(new Event(HOURS_CHANGED_EVENT));
  return out;
}
